import { UIMetadataComplete, Dealer } from '../../../types/UIMetadata';

export const uimetadata: UIMetadataComplete = {
  saveFormConfig: {
    fields: [
      {
        id: 'firstName',
        type: 'text',
        required: true,
        labelKey: 'saveDialog.labelFirstName',
        placeholderKey: 'saveDialog.placeholderFirstName',
      },
      {
        id: 'lastName',
        type: 'text',
        required: true,
        labelKey: 'saveDialog.labelLastName',
        placeholderKey: 'saveDialog.placeholderLastName',
      },
      {
        id: 'email',
        type: 'email',
        required: true,
        labelKey: 'saveDialog.labelEmail',
        placeholderKey: 'saveDialog.placeholderEmail',
        visibility: ['email', 'test-drive', 'callback'],
      },
      {
        id: 'phone',
        type: 'phone',
        required: true,
        labelKey: 'saveDialog.labelMobileNumber',
        placeholderKey: 'saveDialog.placeholderMobileNumber',
        visibility: ['whatsapp', 'test-drive', 'callback'],
      },
      {
        id: 'model',
        type: 'select',
        required: true,
        labelKey: 'saveDialog.labelModel',
        placeholderKey: 'saveDialog.labelModel',
        optionsSource: 'models',
        visibility: ['test-drive', 'callback'],
      },
      {
        id: 'dealer',
        type: 'select',
        required: true,
        labelKey: 'saveDialog.labelDealership',
        placeholderKey: 'saveDialog.placeholderSelectDealership',
        optionsSource: 'dealers',
        visibility: [
          'test-drive',
          'callback',
          'whatsapp',
          'email',
        ],
        // 1.0's save forms show the dealership dropdown but do not gate
        // submission on it; the booking forms and Save to WhatsApp
        // require it (GM needs a dealer on WhatsApp leads); Email stays optional.
        requiredFor: [
          'test-drive',
          'callback',
          'whatsapp',
        ],
      },
      {
        id: 'testDriveDate',
        type: 'date',
        required: true,
        labelKey: 'saveDialog.labelTestDriveDate',
        placeholderKey: 'saveDialog.placeholderTestDriveDate',
        visibility: [
          'test-drive',
        ],
      },
      {
        id: 'testDriveTime',
        type: 'time',
        required: true,
        labelKey: 'saveDialog.labelTestDriveTime',
        placeholderKey: 'saveDialog.placeholderTestDriveTime',
        visibility: [
          'test-drive',
        ],
      },
      {
        id: 'comments',
        type: 'textarea',
        required: false,
        labelKey: 'saveDialog.labelComments',
        placeholderKey: 'saveDialog.placeholderComments',
        visibility: ['test-drive', 'callback'],
      },
    ],
  },
  featureFlags: {
    showPriceInPresentation: false,
    showLogo: true,
    showSpecDetailsButton: true,
    useChevroletFont: true,
    showLocalMatches: true,
    showDisclaimerInPresentation: true,
    // Per-item pricing (card / MODEL / SUMMARY price lines, colours in the total)
    // is opt-in; this project keeps the header-only pricing it has always had.
    showPrices: true, // per-item prices on cards + summary breakdown, like Corvette
    showDisplayCarPrice: true, // Toggle to show car price in CarDisplay UI
  },
  // toggleTabs is now variant-specific — see each variant's toggleTabs field above
  // Global configuration for runtime features
  config: {
    // Display currency + USD conversion factor per region, keyed by the
    // `country` URL parameter (local = usd * fx). Prices were re-derived
    // from the AED figure: usd = AED / 3.68, then usd * fx per country.
    regions: {
      KSA: { label: 'KSA', currency: 'SAR', fx: 3.75 },
      UAE: { label: 'UAE', currency: 'AED', fx: 3.68 },
      OMR: { label: 'Oman', currency: 'OMR', fx: 0.39 },
      BHD: { label: 'Bahrain', currency: 'BHD', fx: 0.38 },
      QAR: { label: 'Qatar', currency: 'QAR', fx: 3.64 },
      JOD: { label: 'Jordan', currency: 'JOD', fx: 0.71 },
      KW: { label: 'Kuwait', currency: 'KWD', fx: 0.31 },
      LBP: { label: 'Lebanon', currency: 'USD', fx: 1 }, // GM quotes Lebanon in USD
      IQD: { label: 'Iraq', currency: 'USD', fx: 1 }, // priced in USD, like Lebanon
      default: { label: 'UAE', currency: 'AED', fx: 3.68 },
    },
    // Lead-side model name — 1.0's `config.model`, which is a lead label
    // distinct from 2.0's `config.model` (the models[] key).
    leadModel: 'captiva phev',
    // Exact model name for the lead file's Model column.
    leadModelName: 'Captiva PHEV',
    // Dial code (prefixed onto mobile_number) and Salesforce lead country
    // code (metadome_country_code), keyed by the `country` URL parameter.
    countryCodes: {
      UAE: {
        code: '+971',
        leadCode: 'AE',
      },
      KSA: {
        code: '+966',
        leadCode: 'SA',
      },
      KW: {
        code: '+965',
        leadCode: 'KW',
      },
      BHD: {
        code: '+973',
        leadCode: 'BH',
      },
      IQD: {
        code: '+964',
        leadCode: 'IQ',
      },
      LBP: {
        code: '+961',
        leadCode: 'LB',
      },
      OMR: {
        code: '+968',
        leadCode: 'OM',
      },
      QAR: {
        code: '+974',
        leadCode: 'QA',
      },
      JOD: {
        code: '+962',
        leadCode: 'JO',
      },
      default: {
        code: '+971',
        leadCode: 'AE',
      },
    },
    model: 'chevrolet-captiva',
    modelName: 'captiva phev',
    pageTitle: 'Captiva Configurator',
    client: 'GM',
    brand: 'chevrolet',
    modelId: 'CHCAPPHEV2026',
    configurationId: 'l25zrwex1u93',
    presentation: {
      video: 'videos/2370/presentation_video_1.webm',
      loadingImage: 'images/2370/sliderImage.webp',
      transitionImage: 'images/2370/transition_logo.png',
      headerLogoKeepColor: true,
    },
    externalAnalytics: {
      provider: 'adobe',
      modelName: 'chevrolet-captiva',
      brand: 'chevrolet',
    },
  },
  models: {
    'chevrolet-captiva': {
      // @ts-expect-error: allow custom 'logo' field not present in UIMetadata type
      logo: 'assets/logos/chevy.svg',
      loadingImage: 'images/2370/sliderImage.webp',
      variants: {
        premier: {
          configurationId: '5vlpab0glq63',
          trim: 'Premier',
          // Captiva only has two real rows (1.0's 3dhotspots: 'First Row Seat'
          // and 'Second Row Seat' - metadata-2370.ts). interiorCamera_2 is an
          // empty placeholder hotspot (no camera messages defined) that
          // duplicated interiorCamera_1's 'Back Row' label in the Interior
          // tab's seat dropdown - hide it the same way Silverado hides its
          // absent rows.
          hiddenHotspots: ['interiorCamera_2'],
          name: {
            en: 'Premier',
            ar: 'Premier',
          },
          image: 'images/2370/variant/captiva_premier.png',
          price: {
            KSA: 111990,
            UAE: 109900,
            OMR: 11647,
            BHD: 11348,
            QAR: 108705,
            JOD: 21204,
            KW: 9258,
            LBP: 29864,
            IQD: 29864,
            default: 109900,
          },
          index: 0,
          isDefault: true,
          ar: {
            enable: false,
            redirection: false,
          },
          // Set mobile/desktop to false to hide that tab for this variant
          toggleTabs: {
            CONFIG:   { mobile: false,  desktop: false },
            MODEL:    { mobile: true,  desktop: true },
            EXTERIOR: { mobile: true,  desktop: true },
            INTERIOR: { mobile: true,  desktop: true },
            OPTIONS:  { mobile: false,  desktop: false },
            SUMMARY:  { mobile: true,  desktop: true },
          },
          autoIntelRPOCode: '1SE',
          brandItemCode: 'CHCA01',
        },
        lt: {
          configurationId: '5vlpab0glq64',
          trim: 'LT',
          hiddenHotspots: ['interiorCamera_2'],
          name: {
            en: 'LT',
            ar: 'LT',
          },
          image: 'images/2370/variant/captiva_premier_lt.png',
          price: {
            KSA: 106895,
            UAE: 104900,
            OMR: 11117,
            BHD: 10832,
            QAR: 103760,
            JOD: 20239,
            KW: 8837,
            LBP: 28505,
            IQD: 28505,
            default: 104900,
          },
          index: 1,
          isDefault: false,
          ar: {
            enable: false,
            redirection: false,
          },
          // Set mobile/desktop to false to hide that tab for this variant
          toggleTabs: {
            CONFIG:   { mobile: false,  desktop: false },
            MODEL:    { mobile: true,  desktop: true },
            EXTERIOR: { mobile: true,  desktop: true },
            INTERIOR: { mobile: true,  desktop: true },
            OPTIONS:  { mobile: false,  desktop: false },
            SUMMARY:  { mobile: true,  desktop: true },
          },
          autoIntelRPOCode: '1SA',
          brandItemCode: 'CHCA02',
        },
      },
      backgrounds: {
        g0ZA968_RpUe: {
          image: 'images/2370/Dome_Desert_Peugeot_D.png',
          title: {
            en: 'City Landscape 1',
            ar: 'غروب الشمس على الشاطئ',
          },
        },
        g0ZA968_RpUd: {
          image: 'images/2370/hdre_025_4k_6_28_LR.png',
          title: {
            en: 'City Landscape 2',
            ar: 'غروب الشمس على الشاطئ',
          },
        },
      },
      ambientLighting: {},
      colors: {
        interior: {
          '1ahry6ef040l': {
            label: {
              en: 'Black Leatherette',
              ar: 'جلدي أسود',
            },
            image: 'images/2370/int_colors/Black_Leatherette.jpg',
            autoIntelItemId: 'fabric-alcantara-gt-code',
            accessoriesToReload: ['az7g6geux1c1'],
            price: {
              KSA: 2038,
              UAE: 2000,
              OMR: 212,
              BHD: 207,
              QAR: 1978,
              JOD: 386,
              KW: 168,
              LBP: 543,
              IQD: 543,
              default: 2000,
            },
            autoIntelRPOCode: '4DG',
            brandItemCode: 'IN17',
          },
          a1e1txmplnmz: {
            label: {
              en: 'Beige Leatherette',
              ar: 'جلد صناعي بيج',
            },
            image: 'images/2370/int_colors/Beige_Leatherette.jpg',
            autoIntelItemId: 'fabric-jacquard-allure-code',
            accessoriesToReload: ['az7g6geux1c1'],
            price: {
              KSA: 2038,
              UAE: 2000,
              OMR: 212,
              BHD: 207,
              QAR: 1978,
              JOD: 386,
              KW: 168,
              LBP: 543,
              IQD: 543,
              default: 2000,
            },
            autoIntelRPOCode: '4AK',
            brandItemCode: 'IN18',
          },
        },
        exterior: {
          vummvpaeqvzy: {
            label: {
              en: 'Khaki Green',
              ar: 'الكاكي الأخضر',
            },
            image: '',
            hex: '#4A4B42',
            autoIntelItemId: 'paint-ingaro-blue-code',
            price: {
              KSA: 2038,
              UAE: 2000,
              OMR: 212,
              BHD: 207,
              QAR: 1978,
              JOD: 386,
              KW: 168,
              LBP: 543,
              IQD: 543,
              default: 2000,
            },
            autoIntelRPOCode: '41U',
            brandItemCode: 'EX48',
          },
          m9hj44coy7uz: {
            label: {
              en: 'Galaxy White',
              ar: 'جالاكسي وايت',
            },
            image: '',
            hex: '#F2F2F2',
            autoIntelItemId: 'paint-obsession-blue-code',
            price: {
              KSA: 2038,
              UAE: 2000,
              OMR: 212,
              BHD: 207,
              QAR: 1978,
              JOD: 386,
              KW: 168,
              LBP: 543,
              IQD: 543,
              default: 2000,
            },
            autoIntelRPOCode: 'GBU',
            brandItemCode: 'EX49',
          },
          b956m640eecz: {
            label: {
              en: 'Laser Grey',
              ar: 'رمادي ليزر',
            },
            image: '',
            hex: '#8C8F91',
            autoIntelItemId: 'paint-okenite-white-code',
            price: {
              KSA: 2038,
              UAE: 2000,
              OMR: 212,
              BHD: 207,
              QAR: 1978,
              JOD: 386,
              KW: 168,
              LBP: 543,
              IQD: 543,
              default: 2000,
            },
            autoIntelRPOCode: 'G1B',
            brandItemCode: 'EX50',
          },
          a8m917apra8f: {
            label: {
              en: 'Shadow Golden',
              ar: 'الظل الذهبي',
            },
            image: '',
            hex: '#7B736B',
            autoIntelItemId: 'paint-perla-nera-black-code',
            price: {
              KSA: 2038,
              UAE: 2000,
              OMR: 212,
              BHD: 207,
              QAR: 1978,
              JOD: 386,
              KW: 168,
              LBP: 543,
              IQD: 543,
              default: 2000,
            },
            autoIntelRPOCode: 'GIE',
            brandItemCode: 'EX51',
          },
          '30kmro8qpmzs': {
            label: {
              en: 'Red Metallic',
              ar: 'أحمر معدني',
            },
            image: '',
            hex: '#A52029',
            autoIntelItemId: 'paint-titane-grey-code',
            price: {
              KSA: 2038,
              UAE: 2000,
              OMR: 212,
              BHD: 207,
              QAR: 1978,
              JOD: 386,
              KW: 168,
              LBP: 543,
              IQD: 543,
              default: 2000,
            },
            autoIntelRPOCode: 'J0U',
            brandItemCode: 'EX52',
          },
          xxvcxp442e0j: {
            label: {
              en: 'Dawn Blue',
              ar: 'الفجر الأزرق',
            },
            image: '',
            hex: '#1E3A59',
            autoIntelItemId: 'paint-artense-grey-code',
            price: {
              KSA: 2038,
              UAE: 2000,
              OMR: 212,
              BHD: 207,
              QAR: 1978,
              JOD: 386,
              KW: 168,
              LBP: 543,
              IQD: 543,
              default: 2000,
            },
            autoIntelRPOCode: 'GKX',
            brandItemCode: 'EX53',
          },
        },
      },
      features: {
        a26pxjlfl6a2: {
          image: 'images/2370/features/Head Lamp.jpg',
          title: {
            en: 'Sleek LED Lights',
            ar: 'أضواء LED أنيقة',
          },
          description: {
            en: 'LED Headlamps and DRL',
            ar: 'مصابيح أمامية LED ومصابيح نهارية',
          },
          type: 'exterior',
        },
        c6cu9zuvl3h3: {
          image: 'images/2370/features/Wheels.jpg',
          title: {
            en: 'Premium Alloy Wheels',
            ar: 'عجلات معدنية فاخرة',
          },
          description: {
            en: '18" Alloy Wheels',
            ar: 'عجلات معدنية مقاس 18 بوصة',
          },
          type: 'exterior',
        },
        k2opkruzrj2d: {
          image: 'images/2370/features/Sunroof.jpg',
          title: {
            en: 'Panoramic Sunroof',
            ar: 'فتحة سقف بانورامية',
          },
          description: {
            en: 'Panoramic Sunroof with Smart Adjustment',
            ar: 'تعديل ذكي لفتحة السقف البانورامية',
          },
          type: 'exterior',
        },
        '8vlg0walqaux': {
          image: 'images/2370/features/Range.jpg',
          title: {
            en: 'Advanced Safety Features',
            ar: 'ميزات السلامة المتقدمة',
          },
          video: 'videos/2370/AEB.webm',
          description: {
            en: 'Advanced safety features with intelligent driving assistance, 360 camera, forward collision alert, lane departure warning, centering and assist and much more',
            ar: 'ميزات أمان متقدمة مع مساعدة القيادة الذكية، وكاميرا 360، وتنبيه الاصطدام الأمامي، وتحذير مغادرة المسار، والتركيز والمساعدة وغير ذلك الكثير',
          },
          type: 'exterior',
        },
        k9s3znjt3lyd: {
          image: 'images/2370/features/Range.jpg',
          title: {
            en: 'Range',
            ar: 'يتراوح',
          },
          video: 'videos/2370/Range.webm',
          description: {
            en: 'Impressive Range: 1000 KM + PHEV to go confidently',
            ar: 'مدى مثير للإعجاب: 1000 كم + سيارة هجينة قابلة للشحن للانطلاق بثقة',
          },
          type: 'exterior',
        },
        aenb8uufp8eq: {
          image: 'images/2370/features/Smart Charging.jpg',
          title: {
            en: 'Smart charging',
            ar: 'الشحن الذكي',
          },
          video: 'videos/2370/charging.webm',
          description: {
            en: 'Engine 1.5L with electric motor and 20.5KW battery – combined range 1000KM. Enjoy every trip without limits and charge energy at your convenience, whether it is electricity or fuel.',
            ar: 'محرك سعة ١.٥ لتر مع محرك كهربائي وبطارية ٢٠.٥ كيلوواط - مدى إجمالي ١٠٠٠ كيلومتر. استمتع بكل رحلة بلا حدود، واشحن طاقتك براحتك، سواءً بالكهرباء أو بالوقود.',
          },
          type: 'exterior',
        },
        xr04kxrfutz8: {
          image: 'images/2370/features/Infotainment Screen.jpg',
          title: {
            en: '15.6" Touchscreen',
            ar: 'شاشة تعمل باللمس مقاس 15.6 بوصة',
          },
          description: {
            en: '15.6" Infotainment Screen with apple carplay & android auto',
            ar: 'شاشة معلومات وترفيه مقاس 15.6 بوصة مع Apple CarPlay وAndroid Auto',
          },
          type: 'interior',
        },
        g8rwatb2qokf: {
          image: 'images/2370/features/Driving Modes.jpg',
          title: {
            en: 'Driving Modes',
            ar: 'أوضاع القيادة',
          },
          description: {
            en: 'Car comes with EV, EV Max and Hybrid driving modes',
            ar: 'تأتي السيارة مع أوضاع القيادة EV و EV Max و Hybrid',
          },
          type: 'interior',
        },
        p0am368ftxdd: {
          image: 'images/2370/features/Driver Information Centre.jpg',
          title: {
            en: 'Driver Information Centre',
            ar: 'مركز معلومات السائق',
          },
          description: {
            en: '8.8” Driver Information Centre',
            ar: 'مركز معلومات السائق مقاس 8.8 بوصة',
          },
          type: 'interior',
        },
        '5sm2vwknvwvg': {
          image: 'images/2370/features/Interior.jpg',
          title: {
            en: 'Refined Spacious Interior',
            ar: 'تصميم داخلي واسع ومُحسّن',
          },
          description: {
            en: 'Spacious 5 seats with foldable seats, ample storage (532 L cargo and 1690 L with folded seats) and added comfort',
            ar: '5 مقاعد واسعة مع مقاعد قابلة للطي، ومساحة تخزين واسعة (532 لترًا من الأمتعة و1690 لترًا مع مقاعد قابلة للطي) وراحة إضافية',
          },
          type: 'interior',
        },
        '14ws7y9o6ph2': {
          image: 'images/2370/features/Seats.jpg',
          title: {
            en: 'Leatherette Seats',
            ar: 'مقاعد جلدية',
          },
          description: {
            en: 'Refined Interior with comfortable leatherette seats and wooden finish dashboard',
            ar: 'تصميم داخلي أنيق مع مقاعد مريحة من الجلد الصناعي ولوحة قيادة خشبية',
          },
          type: 'interior',
        },
        ubxysgr106ml: {
          image: 'images/2370/features/Camera.jpg',
          title: {
            en: '360 Degree Camera',
            ar: 'كاميرا 360 درجة',
          },
          description: {
            en: '360 degree camera to enhance visibility for parking and driving in tight spaces. It helps with Intelligent Driving Assist, Lane Departure Warning and Assist.',
            ar: 'كاميرا بزاوية 360 درجة لتحسين الرؤية عند ركن السيارة والقيادة في الأماكن الضيقة. تدعم نظام مساعدة القيادة الذكي، ونظام تحذير مغادرة المسار، ونظام المساعدة.',
          },
          type: 'interior',
        },
        '4wue2gfl6jkt': {
          image: 'images/2370/features/Power_Mirrors.jpg',
          title: {
            en: 'Power Folding Mirrors',
            ar: 'مرايا قابلة للطي كهربائياً',
          },
          description: {
            en: 'Power Folding Mirrors',
            ar: 'مرايا قابلة للطي كهربائياً',
          },
          type: 'interior',
        },
        '3ud49gqncb2e': {
          image: 'images/2370/features/Head Lamp.jpg',
          title: {
            en: 'LED Headlamps and DRL',
            ar: 'مصابيح أمامية LED ومصابيح نهارية',
          },
          description: {
            en: 'LED Headlamps and DRL',
            ar: 'مصابيح أمامية LED ومصابيح نهارية',
          },
          type: 'exterior',
        },
        '29kx4ja7eg7r': {
          image: 'images/2370/features/Wheels.jpg',
          title: {
            en: '18" Alloy Wheels',
            ar: 'عجلات معدنية مقاس 18 بوصة',
          },
          description: {
            en: '18" Alloy Wheels',
            ar: 'عجلات معدنية مقاس 18 بوصة',
          },
          type: 'exterior',
        },
        '1jhh1m7485kl': {
          image: 'images/2370/features/Modern exterior design.jpg',
          title: {
            en: 'Elevated finishing',
            ar: 'تصميم جسم مرتفع',
          },
          description: {
            en: 'Modern Exterior Design',
            ar: 'التصميم الخارجي الحديث',
          },
          type: 'exterior',
        },
        '8s8tx9p847si': {
          image: 'images/2370/features/Range.jpg',
          title: {
            en: 'Impressive Range: 1000 KM + PHEV to go confidently',
            ar: 'مدى مثير للإعجاب: 1000 كم + سيارة هجينة قابلة للشحن للانطلاق بثقة',
          },
          video: 'videos/2370/Range.webm',
          description: {
            en: '100% Electric Range 410 KM (NEDC) to go confidently',
            ar: 'نطاق هجين يبلغ 59 كيلومترًا (دورة القيادة الأوروبية الجديدة) للانطلاق بثقة',
          },
          type: 'exterior',
        },
        xjhzg2xjvike: {
          image: 'images/2370/features/Smart Charging.jpg',
          title: {
            en: 'Refuel/ Recharge – “Smart and Efficient"',
            ar: 'إعادة التزود بالوقود/الشحن – "ذكي وفعال"',
          },
          video: 'videos/2370/charging.webm',
          description: {
            en: 'Engine 1.5L with electric motor and 20.5KW battery – combined range 1000KM. Enjoy every trip without limits and charge energy at your convenience, whether it is electricity or fuel.',
            ar: 'محرك سعة ١.٥ لتر مع محرك كهربائي وبطارية ٢٠.٥ كيلوواط - مدى إجمالي ١٠٠٠ كيلومتر. استمتع بكل رحلة بلا حدود، واشحن طاقتك براحتك، سواءً بالكهرباء أو بالوقود.',
          },
          type: 'exterior',
        },
        '5axgfh2btytx': {
          image: 'images/2370/features/Infotainment Screen.jpg',
          title: {
            en: '10.1" Touchscreen',
            ar: 'شاشة تعمل باللمس مقاس 10.1 بوصة',
          },
          description: {
            en: '10.1" Infotainment Screen with apple carplay & android auto',
            ar: 'شاشة معلومات وترفيه مقاس 10.1 بوصة مع Apple CarPlay وAndroid Auto',
          },
          type: 'interior',
        },
        bf5spqe7qghc: {
          image: 'images/2370/features/Driver Information Centre.jpg',
          title: {
            en: '8.8” Driver Information Centre',
            ar: 'مركز معلومات السائق مقاس 8.8 بوصة',
          },
          description: {
            en: '8.8” Driver Information Centre',
            ar: 'مركز معلومات السائق مقاس 8.8 بوصة',
          },
          type: 'interior',
        },
        '2je8obbpqtv4': {
          image: 'images/2370/features/Interior.jpg',
          title: {
            en: 'Refined Spacious Interior',
            ar: 'تصميم داخلي واسع ومُحسّن',
          },
          description: {
            en: 'Spacious 5 seats with foldable seats, ample storage (532 L cargo and 1690 L with folded seats) and added comfort',
            ar: '5 مقاعد واسعة مع مقاعد قابلة للطي، ومساحة تخزين واسعة (532 لترًا من الأمتعة و1690 لترًا مع مقاعد قابلة للطي) وراحة إضافية',
          },
          type: 'interior',
        },
        '2kzy6a0jhsu2': {
          image: 'images/2370/features/Seats.jpg',
          title: {
            en: 'Refined Interior with comfortable leatherette seats and wooden finish dashboard',
            ar: 'تصميم داخلي أنيق مع مقاعد مريحة من الجلد الصناعي ولوحة قيادة خشبية',
          },
          description: {
            en: 'Refined Interior with comfortable leatherette seats and wooden finish dashboard',
            ar: 'تصميم داخلي أنيق مع مقاعد مريحة من الجلد الصناعي ولوحة قيادة خشبية',
          },
          type: 'interior',
        },
        '4cq85subl38y': {
          image: 'images/2370/features/Camera.jpg',
          title: {
            en: '360 Degree Camera',
            ar: 'كاميرا 360 درجة',
          },
          video: 'videos/2370/AEB.webm',
          description: {
            en: '360 degree camera to enhance visibility for parking and driving in tight spaces. It helps with Intelligent Driving Assist, Lane Departure Warning and Assist.',
            ar: 'كاميرا بزاوية 360 درجة لتحسين الرؤية عند ركن السيارة والقيادة في الأماكن الضيقة. تدعم نظام مساعدة القيادة الذكي، ونظام تحذير مغادرة المسار، ونظام المساعدة.',
          },
          type: 'interior',
        },
        '2hc2o7cuwka9': {
          image: 'images/2370/features/Power_Mirrors.jpg',
          title: {
            en: 'Power Folding Mirrors',
            ar: 'مرايا قابلة للطي كهربائياً',
          },
          description: {
            en: 'Power Folding Mirrors',
            ar: 'مرايا قابلة للطي كهربائياً',
          },
          type: 'interior',
        },
      },
      accessories: {
        '0ze58kdiphrh': {
          // Hidden default (display:false, isDefault:true in graphics-metadata) -
          // never shown as a selectable card, so it's standard equipment already
          // baked into the variant's base price. isDefault must match
          // graphics-metadata so App.tsx's total-price calc excludes it (it
          // explicitly skips acc.isDefault items) instead of billing it again.
          isDefault: true,
          label: {
            en: 'Sunroof',
            ar: 'فتحة السقف',
          },
          image: 'images/2370/with_sunroof.png',
          category: {
            en: 'Roof',
            ar: 'سَطح',
          },
          price: {
            KSA: 5386,
            UAE: 5285,
            OMR: 560,
            BHD: 546,
            QAR: 5228,
            JOD: 1020,
            KW: 445,
            LBP: 1436,
            IQD: 1436,
            default: 5285,
          },
          cardType: 'cart',
          brandItemCode: 'OP01',
        },
        az7g6geux1c1: {
          // Hidden default (display:false, isDefault:true in graphics-metadata) -
          // an internal seat-material texture swap driven by interior colour, not a
          // real selectable accessory. label/image/category/price were copy-pasted
          // from the Sunroof entry above; isDefault must match graphics-metadata so
          // App.tsx's total-price calc excludes it (it explicitly skips
          // acc.isDefault items) instead of wrongly billing its stale $5,285.
          isDefault: true,
          label: {
            en: 'Seat',
            ar: 'فتحة السقف',
          },
          image: 'images/2370/with_sunroof.png',
          category: {
            en: 'Roof',
            ar: 'سَطح',
          },
          price: {
            KSA: 5386,
            UAE: 5285,
            OMR: 560,
            BHD: 546,
            QAR: 5228,
            JOD: 1020,
            KW: 445,
            LBP: 1436,
            IQD: 1436,
            default: 5285,
          },
          cardType: 'cart',
          brandItemCode: 'AC94',
        },
      },
      packages: {},
      interactions: {
        i7vl1vjogg4n: {
          label: {
            en: 'Doors',
            ar: 'الأبواب',
          },
          type: 'interaction',
          icon: 'assets/icons/interactions/doors.svg',
        },
        tsaymqochh74: {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة',
          },
          type: 'interaction',
          icon: 'assets/icons/interactions/boot.svg',
        },
        u18c8goidt2c: {
          label: {
            en: 'Headlight',
            ar: 'ضوء السقف',
          },
          type: 'interaction',
          icon: 'assets/icons/interactions/headlight.svg',
        },
        qpa1nen87vah: {
          label: {
            en: 'DRL',
            ar: 'DRL',
          },
          type: 'interaction',
          icon: 'assets/icons/interactions/drl.svg',
        },
        etgu38d7vg8q: {
          label: {
            en: 'Taillight',
            ar: 'الضوء الخلفي',
          },
          type: 'interaction',
          icon: 'assets/icons/interactions/taillight.svg',
        },
        '1dzdmvhl8vp23': {
          label: {
            en: 'Sunroof',
            ar: 'الضوء الخلفي',
          },
          type: 'interaction',
          icon: 'assets/icons/interactions/sunroof.svg',
        },
        o1jxfnah7tvf: {
          label: {
            en: '2nd Row Folding',
            ar: 'الصف الثاني قابل للطي',
          },
          type: 'interaction',
          icon: 'assets/icons/interactions/seat.svg',
        },
      },
      hotspots: {
        exteriorCamera: {
          title: {
            en: 'Exterior',
            ar: 'الخارج',
          },
        },
        interiorCamera: {
          title: {
            en: 'Front Row',
            ar: 'الصف الأمامي',
          },
        },
        interiorCamera_1: {
          title: {
            en: 'Back Row',
            ar: 'الصف الخلفي',
          },
        },
        interiorCamera_2: {
          title: {
            en: 'Back Row',
            ar: 'الصف الخلفي',
          },
        },
      },
      specifications: {
        heading: {
          en: 'All-new Chevrolet Captiva PHEV',
          ar: 'All-new Chevrolet Captiva PHEV',
        },
        trim: {
          en: 'FWD',
          ar: 'FWD',
        },
        price: {
          en: 'From AED 75,000',
          ar: 'من 75,000 درهم',
        },
        model: 'Captiva PHEV',
        seatingCapacity: '7',
        dimensions: {
          length: '4840 mm',
          width: '1954 mm',
          height: '1646 mm',
          wheelbase: '2954 mm',
          groundClearance: '163 mm',
        },
        performance: [
          {
            value: '201',
            unit: '',
            label: {
              en: 'Horsepower',
              ar: 'حصان',
            },
          },
          {
            value: '310',
            unit: 'nm',
            label: {
              en: 'Max Torque',
              ar: 'أقصى عزم دوران',
            },
          },
          {
            value: '1000+',
            unit: 'km',
            label: {
              en: 'Range*',
              ar: 'مدى*',
            },
          },
        ],
        summary: [
          {
            title: {
              en: 'Model',
              ar: 'نموذج',
            },
            subtitle: {
              en: 'All-new Chevrolet Captiva PHEV',
              ar: 'All-new Chevrolet Captiva PHEV',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Exterior Colors',
              ar: 'الألوان الخارجية',
            },
            subtitle: {
              en: 'Galaxy White, Laser Gray, Shadow Golden, Red Metallic, Dawn Blue, Khaki Green',
              ar: 'أبيض جلاكسي، رمادي ليزري، ذهبي ظلي، أحمر معدني، أزرق فجر، أخضر كاكي',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Interior',
              ar: 'الداخلية',
            },
            subtitle: {
              en: 'Black Leatherette or Beige Leatherette',
              ar: 'جلد صناعي أسود أو جلد صناعي بيج',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Wheelbase',
              ar: 'قاعدة العجلات',
            },
            subtitle: {
              en: '2800 mm',
              ar: '2800 mm',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Cargo Space',
              ar: 'سعة الأمتعة',
            },
            subtitle: {
              en: '532 Litre and 1,609L with folded seats',
              ar: '532 لترًا و1609 لترًا مع مقاعد قابلة للطي',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Powertrain',
              ar: 'مجموعة نقل الحركة',
            },
            subtitle: {
              en: 'Front Wheel Drive',
              ar: 'الدفع بالعجلات الأمامية',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Total Length',
              ar: 'الطول الكلي',
            },
            subtitle: {
              en: '4745 mm',
              ar: '4745 mm',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Seating Capacity',
              ar: 'سعة الجلوس',
            },
            subtitle: {
              en: '5 Seats',
              ar: '5 مقاعد',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Infotainment Screen',
              ar: 'شاشة المعلومات والترفيه',
            },
            subtitle: {
              en: '15.6 inch/ 10.1 inch',
              ar: '15.6 بوصة / 10.1 بوصة',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
        ],
      },
    },
  },
  staticTranslations: {
    specDetails: {
      model: { en: 'Model', ar: 'الموديل' },
      exteriorColours: { en: 'Exterior Colours', ar: 'ألوان الهيكل' },
      interiorAmbience: { en: 'Interior Ambience', ar: 'الجو الداخلي' },
      seatingCapacity: { en: 'Seating Capacity', ar: 'سعة المقاعد' },
      length: { en: 'Length', ar: 'الطول' },
      width: { en: 'Width', ar: 'العرض' },
      height: { en: 'Height', ar: 'الارتفاع' },
      wheelbase: { en: 'Wheelbase', ar: 'قاعدة العجلات' },
      groundClearance: { en: 'Ground Clearance', ar: 'الخلوص الأرضي' },
      share: { en: 'Share', ar: 'شارك' },
    },
    variantSelection: {
      title: { en: 'Models', ar: 'الموديلات' },
      select: { en: 'Select', ar: 'تحديد' },
      cancel: { en: 'Cancel', ar: 'إلغاء' },
    },
    loadingScreen: {
      t1: { en: 'CURATING', ar: 'تنسيق' },
      t2: { en: 'YOUR', ar: 'تجربتك' },
      t3: { en: 'TAHOE', ar: 'تاهو' },
      t4: { en: 'EXPERIENCE', ar: 'تجربة' },
      default: {
        en: 'Preparing the canvas for your dream car, Just a few more seconds to go!',
        ar: 'جاري تجهيز اللوحة لسيارتك الحلم، بضع ثوانٍ أخرى فقط!',
      },
      enterConfigurator: {
        en: 'Build Your Ride',
        ar: 'ابنِ سيارتك',
      },
      loading: { en: 'Loading', ar: 'جارٍ التحميل' },
      variantLoading: { en: 'Variant is loading', ar: 'الفئة جارٍ التحميل' },
      disclaimer: {
        en: 'Disclaimer',
        ar: 'إخلاء المسؤولية',
      },
      termsConditions: {
        en: 'Terms & Conditions',
        ar: 'الشروط والأحكام',
      },
      viewSpecDetails: {
        en: 'View Spec Details',
        ar: 'عرض تفاصيل المواصفات',
      },
      disclaimerContent: {
        en: 'Prices are subject to change without prior notice and may vary based on location, availability, or other factors.',
        ar: 'الأسعار عرضة للتغيير دون إشعار مسبق وقد تختلف بناءً على الموقع والتوافر وعوامل أخرى.',
      },
      termsModal: {
        title: {
          en: 'Configurator Use Terms and Conditions',
          ar: 'شروط وأحكام استخدام المتكون',
        },
        lead: {
          en: 'By using this vehicle configurator and submitting your build, you acknowledge and agree to the following:',
          ar: 'باستخدام أداة تكوين السيارة هذه وإرسال التصميم الخاص بك، فإنك تقر وتوافق على ما يلي:',
        },
        sections: {
          item1: {
            title: { en: '1. Non-Binding Submission', ar: '1. تقديم غير ملزم' },
            content: {
              en: 'Submitting your vehicle configuration does not constitute a formal vehicle order or purchase agreement. No payment or deposit is collected at this stage.',
              ar: 'إن تقديم تكوين سيارتك لا يشكل طلب سيارة رسميًا أو اتفاقية شراء. لا يتم تحصيل أي مبلغ أو وديعة في هذه المرحلة.',
            },
          },
          item2: {
            title: { en: '2. Preliminary Estimate', ar: '2. تقدير أولي' },
            content: {
              en: 'All pricing displayed is Manufacturer’s Suggested Retail Price (MSRP) and is provided for informational purposes only. Final pricing, taxes, fees, and applicable offers will be confirmed by the authorized dealer.',
              ar: 'جميع الأسعار المعروضة هي سعر التجزئة المقترح من الشركة المصنعة (MSRP) ويتم توفيرها لأغراض إعلامية فقط. سيتم تأكيد السعر النهائي والضرائب والرسوم والعروض المطبقة من قبل الوكيل المعتمد.',
            },
          },
          item3: {
            title: { en: '3. Dealer Follow-Up', ar: '3. متابعة الوكيل' },
            content: {
              en: 'Your submitted configuration will be shared with a participating dealer, who will contact you to discuss your preferences, availability, and next steps.',
              ar: 'سيتم مشاركة التكوين الذي أرسلته مع وكيل مشارك، والذي سيتصل بك لمناقشة تفضيلاتك ومدى التوفر والخطوات التالية.',
            },
          },
          item4: {
            title: {
              en: '4. Availability and Fulfillment',
              ar: '4. التوفر والتنفيذ',
            },
            content: {
              en: 'We will make every reasonable effort to fulfill your preferences; however, final vehicle and accessories selection is subject to dealer inventory and production availability. Configurations may be limited to in-stock or incoming vehicles.',
              ar: 'سنبذل كل جهد معقول لتلبية تفضيلاتك؛ ومع ذلك، يخضع الاختيار النهائي للسيارة والملحقات لمخزون الوكيل وتوافر الإنتاج. قد تقتصر التكوينات على المركبات الموجودة في المخزون أو القادمة.',
            },
          },
          item5: {
            title: { en: '5. No Obligation', ar: '5. لا التزام' },
            content: {
              en: 'You are under no obligation to purchase the configured vehicle, and submitting your configuration does not create a contractual relationship between you and the manufacturer or dealer.',
              ar: 'أنت غير ملزم بشراء السيارة التي تم تكوينها، وإرسال التكوين الخاص بك لا ينشئ علاقة تعاقدية بينك وبين الشركة المصنعة أو الوكيل.',
            },
          },
          item6: {
            title: {
              en: '6. Use of Personal Information and Consent',
              ar: '6. استخدام المعلومات الشخصية والموافقة',
            },
            content: {
              en: 'By submitting your configuration, you consent to the collection, use, and sharing of your personal information with authorized dealers to facilitate your request, in accordance with our Privacy Statement.',
              ar: 'من خلال إرسال التكوين الخاص بك، فإنك توافق على جمع معلوماتك الشخصية واستخدامها ومشاركتها مع الوكلاء المعتمدين لتسهيل طلبك، وفقًا لبيان الخصوصية الخاص بنا.',
            },
          },
          item7: {
            title: { en: '7. System Limitations', ar: '7. قيود النظام' },
            content: {
              en: 'While we aim to provide accurate and current information, the configurator may not reflect all available features or up-to-date pricing. Errors or omissions may occur.',
              ar: 'بينما نهدف إلى توفير معلومات دقيقة وحالية، قد لا يعكس المتكون جميع الميزات المتاحة أو الأسعار المحدثة. قد تحدث أخطاء أو سهو.',
            },
          },
        },
      },
    },
    sidePanel: {
      menu: { en: 'Main Menu', ar: 'القائمة الرئيسية' },
      search: { en: 'Search', ar: 'بحث' },
      settings: { en: 'Settings', ar: 'الإعدادات' },
      profile: { en: 'Profile', ar: 'الملف الشخصي' },
      notifications: { en: 'Notifications', ar: 'الإشعارات' },
      help: { en: 'Help & Support', ar: 'المساعدة والدعم' },
      filter: { en: 'Filter Options', ar: 'خيارات التصفية' },
      favorites: { en: 'Favorites', ar: 'المفضلة' },
      history: { en: 'History', ar: 'التاريخ' },
      tools: { en: 'Tools', ar: 'الأدوات' },
      variants: { en: 'Variants', ar: 'الأنواع' },
      exteriorColor: { en: 'Exterior Color', ar: 'لون الهيكل' },
      interiorColor: { en: 'Interior Color', ar: 'ألوان المقصورة' },
      features: { en: 'Features', ar: 'الميزات' },
      step: { en: 'Step', ar: 'خطوة' },
      openPanel: { en: 'Open Panel', ar: 'فتح اللوحة' },
      closePanel: { en: 'Close Panel', ar: 'إغلاق اللوحة' },
    },
    landscape: {
      message: {
        en: 'Please rotate your device to portrait mode',
        ar: 'يرجى تدوير جهازك إلى الوضع الرأسي',
      },
      subtitle: {
        en: 'For the best experience, please turn your device upright.',
        ar: 'للحصول على أفضل تجربة، يرجى إمساك جهازك بشكل رأسي.',
      },
      title1: { en: '3008 - Variant 1', ar: '3008 - النوع 1' },
      subtitle1: { en: 'Urban environment', ar: 'بيئة حضرية' },
      title2: { en: '3008 - Variant 2', ar: '3008 - النوع 2' },
      subtitle2: { en: 'Urban environment', ar: 'بيئة حضرية' },
      title3: { en: '3008 - Variant 3', ar: '3008 - النوع 3' },
      subtitle3: { en: 'Urban environment', ar: 'بيئة حضرية' },
    },
    cameraViewToggle: {
      ext: { en: 'EXT', ar: 'خارجي' },
      int: { en: 'INT', ar: 'داخلي' },
      dualTone: { en: 'Dual-tone', ar: 'لون مزدوج' },
    },

    bottomDrawer: {
      variants: { en: 'Variants', ar: 'الأنواع' },
      interactions: { en: 'Interactions', ar: 'التفاعلات' },
      accessories: { en: 'Options & Accessories', ar: 'الإكسسوارات' },
      features: { en: 'Features', ar: 'الميزات' },
      highlights: { en: 'Highlights', ar: 'الميزات البارزة' },
      moreOptions: { en: 'More Options', ar: 'المزيد من الخيارات' },
      colorSelector: { en: 'Color Selector', ar: 'محدد اللون' },
      exteriorColor: { en: 'Exterior Color', ar: 'لون الهيكل' },
      interiorColor: { en: 'Interior Color', ar: 'لون المقصورة' },
      wheels: { en: 'Wheels', ar: 'العجلات' },
      roofs: { en: 'Roofs', ar: 'الأسقف' },
      performance: { en: 'Performance', ar: 'الأداء' },
      packages: { en: 'Packages', ar: 'الحزم' },
      chargingOptions: { en: 'Charging Options', ar: 'خيارات الشحن' },
      safetyServices: { en: 'Safety & Services', ar: 'السلامة والخدمات' },
      expand: { en: 'Expand', ar: 'توسيع' },
      minimize: { en: 'Minimize', ar: 'تصغير' },
      freeRoamView: { en: 'Free Roam View', ar: 'عرض التجوال الحر' },
      featureView: { en: 'Featured View', ar: 'العرض المميز' },
    },
    viewport: {
      changeBackground: { en: 'Change Background', ar: 'تغيير الخلفية' },
      selectLanguage: { en: 'Switch Language (عربي)', ar: 'تبديل اللغة (EN)' },
      menu: { en: 'Menu', ar: 'القائمة' },
    },
    qr: {
      scan: { en: 'Scan QR', ar: 'امسح رمز الاستجابة السريعة' },
      scanWithPhone: {
        en: 'Scan with your phone to view in your Space',
        ar: 'امسح باستخدام هاتفك لعرضه في مساحتك',
      },
    },
    share: {
      title: { en: 'Share Configuration', ar: 'مشاركة التكوين' },
      description: {
        en: 'Share your vehicle configuration with others',
        ar: 'شارك تكوين سيارتك مع الآخرين',
      },
      twitterTitle: {
        en: 'Check out my Chevrolet configuration!',
        ar: 'شاهد تكوين سيارتي الشيفروليه!',
      },
      whatsappTitle: {
        en: 'Check out my Chevrolet configuration!',
        ar: 'شاهد تكوين سيارتي الشيفروليه!',
      },
      linkedinTitle: {
        en: 'My Chevrolet Configuration',
        ar: 'تكويني لسيارة شيفروليه',
      },
      linkedinSummary: {
        en: 'Check out my custom Chevrolet configuration!',
        ar: 'شاهد تكويني المخصص لسيارة شيفروليه!',
      },
      emailSubject: {
        en: 'Check out my Chevrolet Configuration',
        ar: 'شاهد تكوين سيارتي الشيفروليه',
      },
      emailBody: {
        en: 'I configured my dream Chevrolet. Take a look:',
        ar: 'قمت بتكوين سيارة شيفروليه المثالية. ألق نظرة:',
      },
      linkLabel: { en: 'Or copy link:', ar: 'أو انسخ الرابط:' },
      copied: {
        en: 'Copied',
        ar: 'تم نسخ الرابط إلى الحافظة!',
      },
      copy: { en: 'Copy', ar: 'نسخ' },
      saveAndShare: { en: 'Save & Share', ar: 'حفظ ومشاركة' },
      copyLink: { en: 'Copy link', ar: 'نسخ الرابط' },
      facebook: { en: 'Facebook', ar: 'Facebook' },
      twitter: { en: 'Twitter', ar: 'Twitter' },
      gmail: { en: 'Gmail', ar: 'Gmail' },
      whatsapp: { en: 'Whatsapp', ar: 'Whatsapp' },
    },
    topBar: {
      matchingVehiclesFound: {
        en: 'Matching Vehicles Found',
        ar: 'تم العثور على المركبات المطابقة',
      },
    },
    bottomBar: {
      priceLabel: { en: 'ex showroom price', ar: 'سعر صالة العرض' },
      leads: { en: 'Save', ar: 'حفظ' },
      findLocalMatches: {
        en: 'Find Local Matches',
        ar: 'اعثر على التطابقات المحلية',
      },
      save: { en: 'Save', ar: 'حفظ' },
      item: { en: 'Item', ar: 'العنصر' },
      items: { en: 'Items', ar: 'عناصر' },
      totalToPay: { en: 'Total to pay', ar: 'الإجمالي للدفع' },
      bookTestDrive: { en: 'Book a Test Drive', ar: 'حجز تجربة قيادة' },
      requestQuote: { en: 'Request a Quote', ar: 'طلب عرض سعر' },
      downloadBrochure: { en: 'Download Brochure', ar: 'تحميل الكتيب' },
    },
    priceSummary: {
      title: { en: 'Price Summary', ar: 'ملخص السعر' },
      enterLocation: { en: 'Enter Location', ar: 'أدخل الموقع' },
      selectCountry: { en: 'Location', ar: 'الموقع' },
      selectCity: { en: 'Location', ar: 'الموقع' },
      note: {
        en: 'Note : Prices might vary by state and all prices listed are on road price, T&C apply.',
        ar: 'ملاحظة: قد تختلف الأسعار حسب الولاية وجميع الأسعار المذكورة هي الأسعار على الطريق، تطبق الشروط والأحكام.',
      },
      breakdown: { en: 'Price Breakdown', ar: 'تفصيل السعر' },
      color: { en: 'Color', ar: 'اللون' },
      variant: { en: 'Variant', ar: 'الفئة' },
      included: { en: 'Included', ar: 'مشمول' },
      total: { en: 'Total', ar: 'الإجمالي' },
    },
    // viewToggle removed as it was duplicate and unused
    common: {
      exit: { en: 'Exit', ar: 'خروج' },
      close: { en: 'Close', ar: 'إغلاق' },
      cancel: { en: 'Cancel', ar: 'إلغاء' },
      back: { en: 'Back', ar: 'رجوع' },
      next: { en: 'Next', ar: 'التالي' },
      done: { en: 'Done', ar: 'تم' },
      exterior: { en: 'Exterior', ar: 'خارجي' },
      interior: { en: 'Interior', ar: 'داخلي' },
      enterFullscreen: { en: 'Enter Fullscreen', ar: 'ملء الشاشة' },
      exitFullscreen: { en: 'Exit Fullscreen', ar: 'إلغاء ملء الشاشة' },
      selectTrim: { en: 'Select Trim', ar: 'اختر الفئة' },
      heading: { en: 'Standard Features', ar: 'المميزات  القياسية' },
      description: {
        en: 'Chevrolet Arabia',
        ar: 'شيفروليه العربية',
      },
      goBack: { en: 'Go Back', ar: 'العودة' },
      freeRoamView: { en: 'Free Roam View', ar: 'عرض التجوال الحر' },
      featureView: { en: 'Featured View', ar: 'عرض مميز' },
      buildYourRide: { en: 'Build Your Ride', ar: 'ابنِ سيارتك' },
    },
    cart: {
      title: { en: 'Your Order', ar: 'طلبك' },
      disclaimer: {
        en: 'Accessory prices and availability will be confirmed at the showroom',
        ar: 'سيتم تأكيد أسعار الملحقات وتوافرها في صالة العرض.',
      },
      exteriorLabel: { en: 'Exterior:', ar: ':خارجي' },
      interiorLabel: { en: 'Interior:', ar: ':داخلي' },
      addedAccessories: { en: 'Added Accessories', ar: 'الإكسسوارات المضافة' },
      priceEstimate: { en: 'Price Estimate', ar: 'تقدير السعر' },
      priceBreakdown: { en: 'Price Breakdown', ar: 'تفاصيل السعر' },
      reserveNow: { en: 'Proceed', ar: 'متابعة' },
      removeAccessory: { en: 'Remove accessory', ar: 'إزالة الإكسسوار' },
      removeItem: { en: 'Remove', ar: 'إزالة' },
      packageCategory: { en: 'Package', ar: 'حزمة' },
    },
    saveDialog: {
      // Option screen
      optionsTitle: {
        en: 'How would you like to proceed?',
        ar: 'كيف ترغب في المتابعة؟',
      },
      optionWhatsapp: { en: 'Save to WhatsApp', ar: 'حفظ على واتساب' },
      optionEmail: { en: 'Save to Email', ar: 'حفظ على البريد الإلكتروني' },
      optionTestDrive: { en: 'Book a Test Drive', ar: 'احجز اختبار قيادة' },
      optionCallback: { en: 'Request a call Back', ar: 'طلب معاودة الاتصال' },

      // Form field labels
      labelFirstName: { en: 'First Name', ar: 'الاسم الأول' },
      labelLastName: { en: 'Last Name', ar: 'اسم العائلة' },
      labelEmail: { en: 'Email', ar: 'البريد الإلكتروني' },
      labelMobileNumber: { en: 'Mobile Number', ar: 'رقم الهاتف المحمول' },
      labelPrefix: { en: 'Prefix', ar: 'البادئة' },
      labelModel: { en: 'Model', ar: 'الطراز' },
      labelDealership: { en: 'Preferred Dealership', ar: 'الوكالة المفضلة' },
      labelComments: { en: 'Comments', ar: 'التعليقات' },

      // Form placeholders
      placeholderFirstName: { en: 'First Name*', ar: '*الاسم الأول' },
      placeholderLastName: { en: 'Last Name*', ar: '*اسم العائلة' },
      placeholderEmail: { en: 'Email*', ar: '*البريد الإلكتروني' },
      placeholderMobileNumber: {
        en: 'Mobile Number*',
        ar: '*رقم الهاتف المحمول',
      },
      labelTestDriveDate: {
        en: 'Preferred date*',
        ar: 'التاريخ المفضل*',
      },
      labelTestDriveTime: {
        en: 'Preferred time*',
        ar: 'الوقت المفضل*',
      },
      placeholderTestDriveDate: {
        en: 'Preferred date*',
        ar: 'التاريخ المفضل*',
      },
      placeholderTestDriveTime: {
        en: 'Preferred time*',
        ar: 'الوقت المفضل*',
      },
      placeholderComments: { en: 'Comment', ar: 'تعليق' },
      placeholderSelectDealership: {
        en: 'Select Dealership',
        ar: 'اختر الوكالة',
      },

      // Validation error messages
      validationFirstNameRequired: {
        en: 'First Name is required',
        ar: 'الاسم الأول مطلوب',
      },
      validationLastNameRequired: {
        en: 'Last Name is required',
        ar: 'اسم العائلة مطلوب',
      },
      validationEmailRequired: {
        en: 'Email is required',
        ar: 'البريد الإلكتروني مطلوب',
      },
      validationEmailInvalid: {
        en: 'Invalid email format',
        ar: 'تنسيق البريد الإلكتروني غير صحيح',
      },
      validationPhoneRequired: {
        en: 'Mobile Number is required',
        ar: 'رقم الهاتف المحمول مطلوب',
      },
      validationPhoneInvalid: {
        en: 'Mobile Number must be 9 digits',
        ar: 'يجب أن يكون رقم الهاتف المحمول 9 أرقام',
      },
      validationDealerRequired: {
        en: 'Dealer selection is required',
        ar: 'اختيار الوكيل مطلوب',
      },
      validationSubmitFailed: {
        en: 'Failed to send. Please try again later.',
        ar: 'فشل الإرسال. الرجاء المحاولة لاحقًا.',
      },

      // Privacy section
      privacyTitle: {
        en: 'Privacy statement & legal disclaimer',
        ar: 'بيان الخصوصية وإخلاء المسؤولية القانونية',
      },
      privacyText: {
        en: 'I acknowledge and understand that my information will be shared with General Motors, its affiliates, dealer network or other part...',
        ar: 'أقر وأفهم أنه سيتم مشاركة معلوماتي مع جنرال موتورز وشركاتها التابعة وشبكة الوكلاء أو أطراف أخرى...',
      },
      privacyReadMore: { en: 'Read more', ar: 'اقرأ المزيد' },
      privacyMarketingConsent: {
        en: 'I wish to receive any marketing information or have my information shared with third parties for purposes of providing me with marketing information.',
        ar: 'أرغب في تلقي أي معلومات تسويقية أو مشاركة معلوماتي مع أطراف ثالثة لغرض تزويدي بمعلومات تسويقية.',
      },

      // Action buttons
      buttonSend: { en: 'Send Configuration', ar: 'إرسال التكوين' },
      buttonSending: { en: 'Sending...', ar: 'جاري الإرسال...' },
      buttonRetry: { en: 'Retry', ar: 'إعادة المحاولة' },
      buttonClose: { en: 'Okay, Got it', ar: 'حسنًا، فهمت' },

      // Success screen
      successTitle: { en: 'Enquiry Submitted', ar: 'تم تقديم الاستفسار' },
      successLabelModel: { en: 'Model name', ar: 'اسم الطراز' },
      successLabelVariant: { en: 'Variant name', ar: 'اسم الفئة' },
      successLabelName: { en: 'Name', ar: 'الاسم' },
      successLabelEmail: { en: 'Email', ar: 'البريد الإلكتروني' },
      successLabelMobile: { en: 'Mobile', ar: 'الهاتف المحمول' },

      // Error screen
      qrTitle: {
        en: 'Your Unique Configuration QR',
        ar: 'رمز الاستجابة السريعة لتكوينك',
      },
      buttonSent: {
        en: 'Sent!',
        ar: 'مرسل!',
      },
      qrDescription: {
        en: 'Show this QR code to your nearest dealer to access your saved configuration.',
        ar: 'أظهر رمز الاستجابة السريعة هذا لتاجر التجزئة الأقرب للوصول إلى التكوين المحفوظ.',
      },
      qrNoteLabel: {
        en: 'Note:',
        ar: 'ملاحظة:',
      },
      qrNote: {
        en: 'Only authorized dealers can view this configuration.',
        ar: 'يمكن للوكلاء المعتمدين فقط عرض هذا التكوين.',
      },
      qrDownload: {
        en: 'Download QR',
        ar: 'تحميل QR',
      },
      qrSave: {
        en: 'Save QR',
        ar: 'حفظ QR',
      },
      errorTitle: { en: 'Submission Failed', ar: 'فشل التقديم' },
      errorMessage: {
        en: 'We encountered an error, please try again.',
        ar: 'واجهنا خطأ، يرجى المحاولة مرة أخرى.',
      },
    },
    featureDialog: {
      playPause: { en: 'Play-Pause', ar: 'تشغيل-إيقاف' },
    },
    selectDealership: {
      title: { en: 'Select a Dealer', ar: 'اختر وكيلاً' },
      subtitle: {
        en: 'Please select a dealer to find local matches similar to your configuration',
        ar: 'يرجى اختيار وكيل للعثور على سيارات مطابقة لتكوينك في منطقتك',
      },
      city: { en: 'Abu Dhabi', ar: 'أبو ظبي' }, // Placeholder city
      dealersIn: { en: 'Dealers in', ar: 'وكلاء في' },
      dealersInCity: { en: 'Dealers in {{city}}', ar: 'وكلاء في {{city}}' },
      proceed: {
        en: 'Select Dealer and Proceed to Configurator',
        ar: 'اختر الوكيل وتابع إلى المتكون',
      },
    },
    localMatches: {
      exterior: { en: 'Exterior', ar: 'خارجي' },
      interior: { en: 'Interior', ar: 'داخلي' },
      dualTone: { en: 'Dual-tone', ar: 'لون مزدوج' },
      yourConfiguration: { en: 'Your Configuration', ar: 'تكوينك' },
      viewConfiguration: {
        en: 'View your configuration details',
        ar: 'عرض تفاصيل تكوينك',
      },
      yourBuildDetails: { en: 'Your Build Details', ar: 'تفاصيل بنائك' },
      options: { en: 'Options', ar: 'الخيارات' },
      save: { en: 'Save', ar: 'حفظ' },
      dealersIn: { en: 'Dealers in', ar: 'وكلاء في' },
      changeDealer: { en: 'Change Dealer', ar: 'تغيير الوكيل' },
      carsAvailable: {
        en: 'Cars Available at the Selected Dealership',
        ar: 'السيارات المتوفرة لدى الوكيل المختار',
      },
      matchesFound: { en: 'Matches found', ar: 'تطابق تم العثور عليه' },
      startingFrom: { en: 'Starting from', ar: 'يبدأ من' },
      getQuote: { en: 'Proceed', ar: 'احصل على عرض سعر' },
      viewDifferences: { en: 'View Differences', ar: 'عرض الاختلافات' },
      match: { en: 'Match', ar: 'تطابق' },
      closestMatchFound: {
        en: 'Closest Match Found',
        ar: 'أقرب تطابق تم العثور عليه',
      },
      buildDetails: { en: 'Build Details', ar: 'تفاصيل التكوين' },
      trim: { en: 'Trim', ar: 'الفئة' },
      matchScoreTooltip: {
        en: 'This score indicates how closely this vehicle matches your configuration.',
        ar: 'يشير هذا النتيجة إلى مدى تطابق هذه السيارة مع تكوينك.',
      },
      whatYouConfigured: { en: 'What you configured', ar: 'ما قمت بتكوينه' },
      matchingInStock: { en: 'Matching vehicle available in stock', ar: 'مركبة مطابقة متوفرة في المخزون' },
    },
    localMatchesNudge: {
      title: {
        en: 'Configurations Found',
        ar: 'تكوينات تم العثور عليها',
      },
      message: {
        en: 'We Found a few local matches similar to your current configuration. Click to view Matches',
        ar: 'وجدنا بعض التطابقات المحلية المشابهة لتكوينك الحالي. انقر لعرض التطابقات',
      },
    },
    featureGroups: {
      exteriorDesign: {
        en: 'Exterior Design & Styling',
        ar: 'التصميم الخارجي والتصميم',
      },
      interiorComfort: {
        en: 'Interior Comfort & Features',
        ar: 'الراحة والميزات الداخلية',
      },
      technologyConvenience: {
        en: 'Technology & Convenience',
        ar: 'التكنولوجيا والراحة',
      },
      safetyAssistance: {
        en: 'Safety & Driver Assistance',
        ar: 'السلامة ومساعدة السائق',
      },
    },
    trim: {
      selection: { en: 'Trim Selection', ar: 'اختيار الفئة' },
    },
    paymentPage: {
      exteriorColor: { en: 'Exterior Color', ar: 'لون الهيكل' },
      interiorColor: { en: 'Interior Color', ar: 'لون المقصورة' },
      accessory: { en: 'Accessory', ar: 'ملحق' },
      closePayment: { en: 'Close', ar: 'إغلاق' },
    },
    packageModal: {
      includes: { en: 'Includes:', ar: 'يشمل:' },
      of: { en: 'of', ar: 'من' },
      totalPriceChange: {
        en: 'Total Price Change:',
        ar: 'إجمالي تغيير السعر:',
      },
      item: { en: 'Item', ar: 'غرض' },
      items: { en: 'Items', ar: 'أغراض' },
      addPackage: { en: 'Add Package', ar: 'إضافة الحزمة' },
      noThanks: { en: 'No Thanks', ar: 'لا شكراً' },
      alreadyApplied: { en: 'Already Applied', ar: 'مُطبّق بالفعل' },
    },
  },
  dealers: {
    UAE: [
      {
        id: 0,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122255',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Quoz 3 Industrial Area Sheikh Zayed Road, Dubai',
          ar: 'الكندي للسيارات - القوز 3، المنطقة الصناعية، طريق الشيخ زايد، دبي',
        },
      },
      {
        id: 1,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256427',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Double Cola Road, Sharjah',
          ar: 'الكندي للسيارات - طريق دبل كولا، الشارقة',
        },
      },
      {
        id: 2,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256428',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Madhab Mohammad Bin Mattar Street, Fujairah',
          ar: 'الغاندي للسيارات - شارع مذهب محمد بن مطر، الفجيرة',
        },
      },
      {
        id: 3,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256429',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Nadiyah Sheikh Mohammed Bin Salem Road, Ras Al Khaimah',
          ar: 'الكندي للسيارات - الندية طريق الشيخ محمد بن سالم، رأس الخيمة',
        },
      },
      {
        id: 4,
        country: 'UAE',
        value: 'UAE',
        dealerId: '301597',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Itihad Road - showroom , Al Khabaisi, Deira, Dubai',
          ar: 'الكندي للسيارات - طريق الاتحاد - صالة عرض , الخبيصي, ديرة, دبي',
        },
      },
      {
        id: 8,
        country: 'UAE',
        value: '',
        dealerId: '334821',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi - Mirdiff City Center',
          ar: 'الغاندي - مردف سيتي سنتر',
        },
      },
      {
        id: 5,
        country: 'UAE',
        value: 'UAE',
        dealerId: '323482',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Madinat Zayed, Al Dhafra',
          ar: 'بن حمودة للسيارات - مدينة زايد، الظفرة',
        },
      },
      {
        id: 6,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122552',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Al Ain',
          ar: 'بن حمودة للسيارات - العين',
        },
      },
      {
        id: 7,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122233',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Abu Dhabi Main',
          ar: 'بن حمودة للسيارات - أبوظبي الرئيسية',
        },
      },
    ],
    KSA: [
      {
        id: 0,
        country: 'KSA',
        value: 'KSA',
        dealerId: '307261',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Al Aziziyah Madina Road, Jeddah',
          ar: 'الجميح للسيارات - العزيزية طريق المدينة، جدة',
        },
      },
      {
        id: 1,
        country: 'KSA',
        value: 'KSA',
        dealerId: '164034',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Hail City King Abdullah Road. Hail',
          ar: 'الجميح للسيارات - مدينة حائل طريق الملك عبدالله. يشيد',
        },
      },
      {
        id: 2,
        country: 'KSA',
        value: 'KSA',
        dealerId: '164035',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Alshlhoub King Abdullah Road, Al Jouf',
          ar: 'الجميح للسيارات - الشلهوب طريق الملك عبدالله، الجوف',
        },
      },
      {
        id: 3,
        country: 'KSA',
        value: 'KSA',
        dealerId: '307271',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Qassim King Abdulaziz Road, Buraidah',
          ar: 'الجميح للسيارات - القصيم طريق الملك عبد العزيز، بريدة',
        },
      },
      {
        id: 4,
        country: 'KSA',
        value: 'KSA',
        dealerId: '260921',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Al Jededah Road, Arar',
          ar: 'الجميح للسيارات - طريق الجديدة، عرعر',
        },
      },
    ],
    KW: [
      {
        id: 0,
        country: 'KW',
        value: 'KW',
        dealerId: '122620',
        dealerName: 'Alamana Auto',
        label: {
          en: 'Alamana Auto - Al Rai 4th Ring Road, Al Rai',
          ar: 'الامانة للسيارات - الري الطريق الدائري الرابع، الري',
        },
      },
      {
        id: 1,
        country: 'KW',
        value: 'KW',
        dealerId: '174979',
        dealerName: 'Alamana Auto',
        label: {
          en: 'Alamana Auto - Fahaheel Industrial Area Dabbous Street, Fahaheel',
          ar: 'الامانة للسيارات - الفحيحيل الصناعية شارع الدبوس، الفحيحيل',
        },
      },
    ],
    BHD: [
      {
        id: 0,
        country: 'BHD',
        value: 'BHD',
        dealerId: '122215',
        dealerName: 'National Motor Company W.L.L',
        label: {
          en: 'National Motor Company W.L.L, Al Qarya 475 Shk. Jaber Al Subah Hwy',
          ar: 'الشركة الوطنية للسيارات ذ.م.م، القرية 475 شارع الشيخ. طريق جابر الصباح',
        },
      },
    ],
    IQD: [
      {
        id: 0,
        country: 'IQD',
        value: 'IQD',
        dealerId: '295876',
        dealerName: 'MAC International FZE',
        label: {
          en: 'MAC International FZE, Al-Karada Sadoun Street',
          ar: 'شركة ماك الدولية (منطقة حرة)، شارع الكرادة سعدون',
        },
      },
      {
        id: 1,
        country: 'IQD',
        value: 'IQD',
        dealerId: '295879',
        dealerName: 'MAC International FZE',
        label: {
          en: 'MAC International FZE, Kurdistan District 100 m Street',
          ar: 'ماك إنترناشونال إف زي إي، منطقة كردستان، شارع ١٠٠ متر',
        },
      },
    ],
    LBP: [
      {
        id: 0,
        country: 'LBP',
        value: 'LBP',
        dealerId: '122226',
        dealerName: 'IMPEX',
        label: {
          en: 'IMPEX -  Badaro, Near the National Museum , Beirut',
          ar: 'إمبكس - بدارو، بالقرب من المتحف الوطني، بيروت',
        },
      },
    ],
    QAR: [
      {
        id: 0,
        country: 'QAR',
        value: 'QAR',
        dealerId: '274766',
        dealerName: 'Jaidah',
        label: {
          en: 'Jaidah Square Showroom - Al Matar Street Umm Ghuwallina, Doha',
          ar: 'صالة عرض "جايدة سكوير" - شارع المطار، أم غويلينة، الدوحة',
        },
      },
    ],
    OMR: [
      {
        id: 0,
        country: 'OMR',
        value: 'OMR',
        dealerId: '122219',
        dealerName: 'Oman Trading Establisment',
        label: {
          en: 'Oman Trading Establisment LLC, Ghalla Al Maardh Street',
          ar: 'مؤسسة عمان التجارية ش.م.م، شارع غلا المعرض',
        },
      },
      {
        id: 1,
        country: 'OMR',
        value: 'OMR',
        dealerId: '234217',
        dealerName: 'Oman Trading Establisment',
        label: {
          en: 'Oman Trading Establisment LLC, Al Farq Street',
          ar: 'مؤسسة عمان التجارية ذ.م.م، شارع الفرق',
        },
      },
      {
        id: 2,
        country: 'OMR',
        value: 'OMR',
        dealerId: '260896',
        dealerName: 'Oman Trading Establisment',
        label: {
          en: 'Oman Trading Establisment LLC, Hail Al Shumaliah Street',
          ar: 'مؤسسة عمان التجارية ذ.م.م، شارع حيل الشمالية',
        },
      },
    ],
    JOD: [
      {
        id: 0,
        country: 'JOD',
        value: 'JOD',
        dealerId: '189129',
        dealerName: 'Leading Vehicles',
        label: {
          en: 'The Leading Vehicles Co. Ltd, Al Rawabi',
          ar: 'شركة المركبات الرائدة المحدودة، الروابي',
        },
      },
    ],
    default: [
      {
        id: 0,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122255',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Quoz 3 Industrial Area Sheikh Zayed Road, Dubai',
          ar: 'الكندي للسيارات - القوز 3، المنطقة الصناعية، طريق الشيخ زايد، دبي',
        },
      },
      {
        id: 1,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256427',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Double Cola Road, Sharjah',
          ar: 'الكندي للسيارات - طريق دبل كولا، الشارقة',
        },
      },
      {
        id: 2,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256428',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Madhab Mohammad Bin Mattar Street, Fujairah',
          ar: 'الغاندي للسيارات - شارع مذهب محمد بن مطر، الفجيرة',
        },
      },
      {
        id: 3,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256429',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Nadiyah Sheikh Mohammed Bin Salem Road, Ras Al Khaimah',
          ar: 'الكندي للسيارات - الندية طريق الشيخ محمد بن سالم، رأس الخيمة',
        },
      },
      {
        id: 4,
        country: 'UAE',
        value: 'UAE',
        dealerId: '301597',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Itihad Road - showroom , Al Khabaisi, Deira, Dubai',
          ar: 'الكندي للسيارات - طريق الاتحاد - صالة عرض , الخبيصي, ديرة, دبي',
        },
      },
      {
        id: 8,
        country: 'UAE',
        value: '',
        dealerId: '334821',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi - Mirdiff City Center',
          ar: 'الغاندي - مردف سيتي سنتر',
        },
      },
      {
        id: 5,
        country: 'UAE',
        value: 'UAE',
        dealerId: '323482',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Madinat Zayed, Al Dhafra',
          ar: 'بن حمودة للسيارات - مدينة زايد، الظفرة',
        },
      },
      {
        id: 6,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122552',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Al Ain',
          ar: 'بن حمودة للسيارات - العين',
        },
      },
      {
        id: 7,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122233',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Abu Dhabi Main',
          ar: 'بن حمودة للسيارات - أبوظبي الرئيسية',
        },
      },
    ],
  },
  plugins: {
    deferredAssetLoading: false,
  },
  externalScript: {
    dev: {
      head: {
        links: [
          {
            snippet:
              'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-bea0833819b4-development.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet:
              'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true,
          },
        ],
        snippets: [
          {
            snippet: `<!-- Meta Pixel Code -->
<script>
!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(argum
ents)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window,
document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '2090290248171811');
fbq('track', 'PageView');
</script>
<noscript><img height="1" width="1"
style="display:none"
src="https://www.facebook.com/tr?
id=2090290248171811&ev=PageView&noscript=1"
/></noscript>
<!-- End Meta Pixel Code -->
        `,
            iframe: true,
            standalone: true,
          },
        ],
      },
    },
    staging: {
      head: {
        links: [
          {
            snippet:
              'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-77110a86d4ec-staging.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet:
              'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true,
          },
        ],
        snippets: [
          {
            snippet: `<!-- Meta Pixel Code -->
<script>
!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(argum
ents)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window,
document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '2090290248171811');
fbq('track', 'PageView');
</script>
<noscript><img height="1" width="1"
style="display:none"
src="https://www.facebook.com/tr?
id=2090290248171811&ev=PageView&noscript=1"
/></noscript>
<!-- End Meta Pixel Code -->
        `,
            iframe: true,
            standalone: true,
          },
        ],
      },
    },
    main: {
      head: {
        links: [
          {
            snippet:
              'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-85b05c76e071.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet:
              'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true,
          },
        ],
        snippets: [
          {
            snippet: `<!-- Meta Pixel Code -->
<script>
!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(argum
ents)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window,
document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '2090290248171811');
fbq('track', 'PageView');
</script>
<noscript><img height="1" width="1"
style="display:none"
src="https://www.facebook.com/tr?
id=2090290248171811&ev=PageView&noscript=1"
/></noscript>
<!-- End Meta Pixel Code -->
        `,
            iframe: true,
            standalone: true,
          },
        ],
      },
    },
  },
  filterOptions: [
    {
      id: 'trim',
      label: { en: 'Trim', ar: 'الطراز' },
      disabled: false,
      type: 'chip' as const,
      key: 'trim',
      options: [
        {
          id: 'premier',
          label: { en: 'Premier', ar: 'Premier' },
          value: 'Premier',
          checked: false,
          subtitle: { en: 'Top Grade', ar: 'الطراز الأعلى' },
          description: { en: 'Fully loaded with premium features', ar: 'مجهز بالكامل بميزات فاخرة' },
        },
        {
          id: 'lt',
          label: { en: 'LT', ar: 'LT' },
          value: 'LT',
          checked: false,
          subtitle: { en: 'Sport Grade', ar: 'الطراز الرياضي' },
          description: { en: 'Feature-rich comfort and performance', ar: 'راحة وأداء بمميزات متعددة' },
        },
      ],
    },
  ],
  styleConfig: {
    cssVariables: {
      '--default-font-family': 'Chevy Sans',
      '--bold-font-family': 'Chevy Sans Medium',
      '--brand-primary': '#0077D9',
      '--brand-primary-hover': '#0062BA',
      '--brand-secondary': '#0077D9',
      '--brand-accent': '#0077D9',
      '--brand-accent-hover': '#0066c0',
      '--brand-accent-light': '#dee5f2',
      '--brand-surface': '#f2f2f2',
      '--brand-surface-alt': '#f3f4f6',
      '--brand-text': '#000000',
      '--brand-text-secondary': '#666666',
      '--brand-text-tertiary': '#bebebe',
      '--brand-border': '#e5e7eb',
      '--brand-border-selected': '#0077D9',
      '--brand-card-bg': '#ffffff',
      '--brand-toggle-gradient-from': '#0077D9',
      '--brand-toggle-gradient-to': '#0077D9',
      '--brand-toggle-knob-border': '#09f',
      '--brand-accent-secondary': '#EB212B',
      '--brand-card-radius': '10px',
      '--brand-card-radius-sm': '7px',
      '--brand-card-radius-lg': '12px',
      '--brand-button-radius': '6px',
      '--brand-button-radius-sm': '5px',
      '--brand-pill-radius': '50px',
      '--brand-cta-radius': '8px',
      '--brand-button-border': '#0077D9',
      '--brand-dialog-radius': '11px',
      '--brand-dialog-option-radius': '8px',
    },
  },
};
